import type { ColumnDef } from "@tanstack/react-table"
import type { TFunction } from "i18next"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { Badge } from "@/components/ui/badge"
import { PaymentMethodNameEnum } from "@/enums"
import { formatMoney } from "@/helpers/format"
import { DateHelper } from "@/utils"

import { PaymentMethodCell } from "./payment-method-cell"
import { ProcessorInfoPopover } from "./processor-info-popover"
import { RefundInfo } from "./refund-info"
import { getTransactionStatusLabel } from "./transaction-status"
import { UpdatedTransactionsInfo } from "./updated-transactions-info"

const getStatusVariant = (status?: string) => {
  switch (status?.toUpperCase()) {
    case "AUTHORIZED":
    case "CAPTURED":
    case "PAID":
      return "default"
    case "PENDING":
    case "PROCESSING":
      return "secondary"
    case "REFUNDED":
    case "CANCELED":
    case "FAILED":
      return "destructive"
    default:
      return "outline"
  }
}

export const createTransactionsColumns = (
  t: TFunction
): ColumnDef<TransactionsDetailsColumnsType>[] => [
  {
    header: t("orders.transactionsTable.id"),
    accessorKey: "id",
    size: 28,
    enableHiding: false,
    cell: ({ row }) => (
      <div className="flex items-center gap-1">
        <code className="max-w-[120px] truncate rounded bg-muted px-1.5 py-0.5 text-xs">
          {row.original.id}
        </code>
        <ClipboardButton
          text={row.original.id}
          variant="ghost"
          className="h-6 w-6"
        />
      </div>
    ),
  },
  {
    header: t("orders.transactionsTable.payer"),
    accessorKey: "payer",
    cell: ({ row }) => {
      const payer = row.original.payer
      if (!payer?.fullName && !payer?.document) {
        return (
          <span className="text-sm text-muted-foreground">
            -
          </span>
        )
      }
      return (
        <div className="flex flex-col gap-0.5">
          {payer.fullName && (
            <span
              className="max-w-[280px] truncate text-sm font-medium"
              title={
                payer.fullName.length > 40
                  ? payer.fullName
                  : undefined
              }
            >
              {payer.fullName}
            </span>
          )}
          {payer.document && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">
                {payer.document}
              </span>
              <ClipboardButton
                text={payer.document}
                variant="ghost"
                className="h-6 w-6 shrink-0"
              />
            </div>
          )}
        </div>
      )
    },
  },
  {
    header: t("orders.transactionsTable.paymentMethod"),
    accessorKey: "method",
    enableHiding: false,
    cell: ({ row }) => (
      <PaymentMethodCell transaction={row.original} t={t} />
    ),
  },
  {
    header: t("orders.transactionsTable.status"),
    accessorKey: "status",
    cell: ({ row }) => (
      <Badge
        variant={getStatusVariant(row.original.status)}
      >
        {getTransactionStatusLabel(row.original.status, t)}
      </Badge>
    ),
  },
  {
    header: t("orders.transactionsTable.processor"),
    accessorKey: "processorInformation",
    cell: ({ row }) => (
      <ProcessorInfoPopover
        processorInfo={row.original.processorInformation}
        t={t}
      />
    ),
  },
  {
    header: t("orders.transactionsTable.amount"),
    accessorKey: "amount",
    cell: ({ row }) => {
      const allRefunds = [
        ...(row.original.refundedTransactions || []),
        ...(row.original.reversedTransactions || []),
      ]
      // Valores estornados só aparecem para cartão de crédito.
      const hasRefunds =
        allRefunds.length > 0 &&
        row.original.method ===
          PaymentMethodNameEnum.CREDIT_CARD
      const refundedAmount = hasRefunds
        ? allRefunds.reduce((sum, r) => sum + r.amount, 0)
        : 0
      return (
        <div className="flex flex-col gap-1">
          <span className="font-semibold tabular-nums">
            {formatMoney(row.original.amount / 100)}
          </span>
          {hasRefunds && (
            <span className="text-xs text-orange-600">
              -{formatMoney(refundedAmount / 100)}{" "}
              {t(
                "orders.transactionsTable.refunds.refunded"
              )}
            </span>
          )}
        </div>
      )
    },
    footer: ({ table }) => {
      const total = table
        .getFilteredRowModel()
        .rows.reduce(
          (sum, row) => sum + row.original.amount,
          0
        )
      return (
        <span className="font-semibold tabular-nums text-primary">
          {formatMoney(total / 100)}
        </span>
      )
    },
  },
  {
    header: t("orders.transactionsTable.refunds.column"),
    accessorKey: "reversedTransactions",
    cell: ({ row }) => (
      <RefundInfo
        refundedTransactions={
          row.original.refundedTransactions
        }
        reversedTransactions={
          row.original.reversedTransactions
        }
        paymentMethod={row.original.method}
        t={t}
      />
    ),
  },
  {
    header: t("orders.transactionsTable.updates.column"),
    accessorKey: "updatedTransactions",
    cell: ({ row }) => (
      <UpdatedTransactionsInfo
        updatedTransactions={
          row.original.updatedTransactions
        }
        t={t}
      />
    ),
  },
  {
    header: t("orders.transactionsTable.date"),
    accessorKey: "createdAt",
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {DateHelper.format(
          row?.original?.createdAt,
          "dd/MM/yyyy HH:mm"
        )}
      </span>
    ),
  },
]
